import { ReleaseReport } from "@design-controls/types";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  createQColumnHelper,
  DataProvider,
  QCard,
  QCollapse,
  QDataTable,
  QEmptyState,
  QHeading,
  QIconButton,
  QProgress,
  QStack,
  QText,
} from "@qualio/ui-components";
import { useDisclosure } from "@chakra-ui/hooks";
import { downloadFile } from "./downloadFIle";

const columnHelper = createQColumnHelper<ReleaseReport["reports"][0]>();

type Props = {
  data?: ReleaseReport;
  isComplete: boolean;
  isLoading: boolean;
};

export const ReportsTable: React.FC<Props> = ({
  isComplete,
  data,
  isLoading,
}) => {
  const { t } = useTranslation();
  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: true });

  const onDownloadClick = useCallback(
    (record: ReleaseReport["reports"][0]) =>
      !!record.url && downloadFile(record.url),
    [],
  );

  const columns = [
    columnHelper.text((record) => record.url?.split(".").pop()?.toUpperCase(), {
      header: t("reports.table.header.fileType"),
      minWidth: "100px",
      width: "10%",
    }),
    columnHelper.text((record) => record.name?.split("-").pop(), {
      header: t("reports.table.header.title"),
      minWidth: "300px",
      width: "80%",
    }),
    {
      header: " ",
      meta: {
        minWidth: "80px",
        width: "10%",
      },
      cell: ({
        row: { original: row },
      }: {
        row: { original: ReleaseReport["reports"][0] };
      }) => {
        return (
          <QIconButton
            iconName="Download"
            aria-label={t("common.ariaLabels.download")}
            data-cy="download"
            onClick={() => onDownloadClick(row)}
          />
        );
      },
    },
  ];

  return (
    <QCard>
      <QStack
        direction="row"
        justifyContent="space-between"
        padding="12px 16px"
      >
        <QHeading fontSize="md" fontWeight={600}>
          {t("reports.heading")}
        </QHeading>
        {
          <QIconButton
            onClick={onToggle}
            iconName={isOpen ? "ChevronUp" : "ChevronDown"}
            aria-label={
              isOpen
                ? t("reports.collapseExpand.collapse")
                : t("reports.collapseExpand.expand")
            }
          />
        }
        {isLoading && (
          <QStack direction="column">
            <QText fontSize="xs" color="gray.500">
              {t("reports.status.generating")}
            </QText>
            <QProgress isIndeterminate />
          </QStack>
        )}
      </QStack>
      <QCollapse isOpen={isOpen}>
        {isComplete && (
          <DataProvider.Fixed data={data?.reports ?? []}>
            <QDataTable
              columns={columns}
              hideItemCount
              data-cy="document-list"
              enableSorting={false}
              getRowId={(record: ReleaseReport["reports"][0]) =>
                record.name?.split("-").pop()?.trim() ?? ""
              }
            />
          </DataProvider.Fixed>
        )}
        {!isComplete && !isLoading && (
          <QEmptyState
            title={t("reports.emptyState.title")}
            subtitle={t("reports.emptyState.subtitle")}
          />
        )}
      </QCollapse>
    </QCard>
  );
};
